import * as React from 'react';

import { Wrapper, StyledMenu } from './FilterPanel.styles';
import { FilterChip, FilterChipGroup } from 'components/FilterChip';
import { FilterMenuContent } from 'components/FilterMenuContent';
import { ApiContext } from 'context/ApiContext';
import QueryParams, { toggleParamInQuery } from 'context/QueryParams';
import { IFilter, IQueryFilter, ISettingsResponse } from 'utils/types';
import { getPeriodStr } from 'views/Settings/settings.utils';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/localStorage';
import {
  IFilterProp,
  getUpdatedFiltersQueryParams,
  getChipValues,
  MAX_FILTERS_LENGTH,
} from './utils';
import { L1Suffix, L2Suffix } from 'utils/constants';
import queryString from 'query-string';
import { createStoredQuery } from 'utils/api';
import { convertParamsToStoredQuery } from 'utils/storedQuery';
import isEmpty from 'lodash/isEmpty';

interface IParams {
  [key: string]: string | string[];
}

interface IExternalProps {
  disabledFilters?: string[];
  localStorageKey?: string;
}
interface IProps extends IExternalProps {
  filters?: IFilter[];
  settings?: ISettingsResponse;
  setParams: (p: any) => void;
  params?: IParams;
  storedQuery: IParams | null;
}

export class FilterPanel extends React.Component<IProps> {
  filtersBtnRef = React.createRef<Node>();

  componentDidMount() {
    const { localStorageKey, setParams, params } = this.props;

    if (localStorageKey) {
      const filters = getLocalStorageItem(localStorageKey);

      if (!params || isEmpty(params)) {
        if (filters && !isEmpty(filters)) {
          setParams(filters);
        }
      } else {
        setLocalStorageItem(localStorageKey, params);
      }
    }
  }

  onFiltersChange = (filter: IQueryFilter) => {
    const { params, storedQuery } = this.props;
    const activeParams = storedQuery || params;
    const newParams = toggleParamInQuery(filter, activeParams || {}) as any;

    this.updateAppState(newParams);
  };

  onL2FiltersChange = (
    filterId: string,
    selectedFilter: IFilterProp,
    isSelected?: boolean
  ) => {
    const { filters, params, storedQuery } = this.props;
    const activeParams = storedQuery || params;

    if (!filters) {
      return;
    }

    const filterOptions = filters.find((item) => item.id === filterId)?.options;

    if (!filterOptions) {
      return;
    }

    const selectedFilterParams = getUpdatedFiltersQueryParams(
      filterId,
      selectedFilter,
      filterOptions,
      isSelected
    );
    const newParams = {
      ...activeParams,
      [filterId]: undefined,
      ...selectedFilterParams,
    };

    this.updateAppState(newParams);
  };

  updateAppState = (params: {
    [key: string]: string | string[] | undefined;
  }) => {
    const { setParams, localStorageKey } = this.props;

    const query = queryString.stringify(params);

    if (query.length > MAX_FILTERS_LENGTH) {
      createStoredQuery(convertParamsToStoredQuery(params)).then(({ id }) => {
        const clearedParams = Object.keys(params).reduce((acc, item) => {
          return {
            ...acc,
            [item]: undefined,
          };
        }, {});
        setParams({
          ...clearedParams,
          storedQueryId: id,
        });

        if (localStorageKey) {
          setLocalStorageItem(localStorageKey, { storedQueryId: id });
        }
      });
    } else {
      const updatedParams = { storedQueryId: undefined, ...params };

      setParams(updatedParams);

      if (localStorageKey) {
        setLocalStorageItem(localStorageKey, updatedParams);
      }
    }
  };

  clearFilter = (filterIds: string[]) => {
    const { params, storedQuery } = this.props;
    const activeParams = storedQuery || params;

    if (activeParams) {
      const newParams = { ...activeParams } as any;

      filterIds.forEach((filterId) => {
        newParams[filterId] = undefined;
      });

      this.updateAppState(newParams);
    }
  };

  render() {
    const { disabledFilters = [], filters, settings } = this.props;
    if (settings === undefined || filters === undefined) {
      return null;
    }
    const { selectedYear } = settings;

    return (
      <Wrapper>
        <FilterChipGroup>
          <FilterChip label={getPeriodStr(selectedYear)} isDisabled />
          {filters
            .filter((f) => !disabledFilters.includes(f.name))
            .map(({ id, name, options }) => {
              const hasL2Options = options.some((item) => item.options);
              const filterIdsToClear = [
                id,
                ...(hasL2Options
                  ? [`${id}${L1Suffix}`, `${id}${L2Suffix}`]
                  : []),
              ];

              return (
                <StyledMenu
                  disableCloseOnSubmenuClick
                  key={id}
                  button={({ isOpen, ref, onClick }) => {
                    return (
                      <div ref={ref} onClick={onClick as any}>
                        <FilterChip
                          key={id}
                          isSelected={isOpen}
                          label={name}
                          onDismiss={() => this.clearFilter(filterIdsToClear)}
                          values={getChipValues(options)}
                        />
                      </div>
                    );
                  }}
                >
                  <FilterMenuContent
                    options={options}
                    filterId={id}
                    onChange={this.onFiltersChange}
                    onL2Change={this.onL2FiltersChange}
                    onClear={this.clearFilter}
                  />
                </StyledMenu>
              );
            })}
        </FilterChipGroup>
      </Wrapper>
    );
  }
}

export default (props: IExternalProps) => (
  <QueryParams>
    {({ params, setParams }) => (
      <ApiContext.Consumer>
        {({ filters, settings, storedQuery }) => (
          <FilterPanel
            {...props}
            filters={filters}
            params={params}
            settings={settings}
            setParams={setParams}
            storedQuery={storedQuery}
          />
        )}
      </ApiContext.Consumer>
    )}
  </QueryParams>
);
